import React, { useEffect } from "react";
import {
  LinkText,
  Panel,
  Text,
  useResponsive,
} from "@clearabee/ui-library-native";
import { Button } from "components/Core/Button/Button";
import { useTranslation } from "react-i18next";
import { styles, ids } from "./PostcodePricing.styles";
import { Spinner } from "components/Core/Spinner/Spinner";
import { Platform, View } from "react-native";
import { Input } from "components/Core/Input/Input";
import { SKIP_WASTE_TYPES } from "lib/constants/skipWasteTypes";
import { theme } from "@clearabee/ui-library-native";
import { Anchor } from "components/Core/Anchor/Anchor";
import { Container } from "components/Container/Container";
import { Form, useForm } from "lib/hooks/useForm";
import { z } from "zod";
import { postcode } from "lib/schemas";
import { getEnv } from "lib/getEnv";

const getValidationSchema = (categorySku: string) =>
  z.object({
    postcode,
    wasteType: categorySku === "SKIPS" ? z.string().min(1) : z.string(),
  });

export type Values = z.infer<ReturnType<typeof getValidationSchema>>;

interface PostcodePricingProps {
  onSubmit: (values: Values) => void;
  postcode?: string;
  isLoading: boolean;
  isExtended: boolean;
  wasteType?: string;
  categorySku: string;
}

export const PostcodePricing = ({
  onSubmit,
  postcode,
  isLoading,
  isExtended,
  wasteType,
  categorySku,
}: PostcodePricingProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { screens } = useResponsive();

  const initialValues: Values = {
    postcode: postcode ?? "",
    wasteType: wasteType ?? "",
  };

  const form = useForm({
    onSubmit,
    defaultValues: initialValues,
    validationSchema: getValidationSchema(categorySku),
  });
  const { submitForm, watch } = form;
  const values = watch(["postcode", "wasteType"]);

  useEffect(() => {
    if (values.some(Boolean)) {
      submitForm();
    }
  }, []);

  return (
    <Form state={form}>
      {({ Field }) => (
        <Container
          styles={(!isExtended || !screens.xlarge) && { paddingHorizontal: 0 }}
          size={categorySku === "SKIPS" ? "xlarge" : "small"}
        >
          <Panel
            shadow={Platform.OS === "web"}
            styles={styles.panel}
            dataSet={{ media: ids.panel }}
          >
            {categorySku === "SKIPS" && (
              <>
                <View
                  style={styles.skipsWrapper}
                  dataSet={{ media: ids.skipsWrapper }}
                >
                  <Text fontSize="small">
                    {translate(
                      "hwa.components.postcodePricing.skipsPrivateLand",
                    )}
                  </Text>
                  <Anchor
                    routeName="Redirect"
                    params={{
                      url: `${getEnv("WEBSITE_URL")}/contact`,
                    }}
                    newTab
                  >
                    <LinkText
                      styles={{ alignSelf: "flex-start" }}
                      textStyles={{
                        fontSize: theme.fontDefaults.small.fontSize,
                        marginTop: theme.spacing.xsmall2,
                      }}
                    >
                      {translate(
                        "hwa.components.postcodePricing.skipsPrivateLandWhy",
                      )}
                    </LinkText>
                  </Anchor>
                </View>
                <Field
                  name="wasteType"
                  label={translate("common.wasteType")}
                  errorStyles={styles.fieldError}
                  errorBackground
                  invertErrorColors
                  styles={styles.wasteTypeField}
                  labelStyles={{ marginHorizontal: theme.spacing.xsmall2 }}
                >
                  {({ field }) => (
                    <Input.Select
                      {...field}
                      defaultValue={initialValues.wasteType}
                      options={SKIP_WASTE_TYPES.map(({ label, value }) => ({
                        label: translate(label),
                        value,
                      }))}
                      placeholder={translate(
                        "common.form.placeholder.wasteType",
                      )}
                      disabled={isLoading}
                    />
                  )}
                </Field>
              </>
            )}
            <Field
              name="postcode"
              label={translate("common.postcode")}
              styles={{ flex: 2, marginHorizontal: theme.spacing.xsmall2 }}
              errorStyles={styles.fieldError}
              labelStyles={{ marginHorizontal: theme.spacing.xsmall2 }}
              invertErrorColors
              errorBackground
            >
              {({ field }) => (
                <>
                  <View
                    style={{
                      position: "relative",
                      flex: 1,
                    }}
                  >
                    <Input.Text
                      {...field}
                      disabled={isLoading}
                      placeholder={translate(
                        categorySku === "SKIPS"
                          ? "common.form.placeholder.deliveryPostcode"
                          : "common.form.placeholder.collectionPostcode",
                      )}
                    />
                    {isLoading && (
                      <Spinner color="accent" style={styles.spinner} />
                    )}
                  </View>
                </>
              )}
            </Field>
            <View style={styles.submitButtonContainer}>
              <Button
                color="warning"
                size="small"
                type="submit"
                disabled={isLoading}
                onClick={submitForm}
                styles={styles.submitButton}
              >
                {translate("common.getPrices")}
              </Button>
            </View>
          </Panel>
        </Container>
      )}
    </Form>
  );
};
